// Campaign attribution (lp, utm_*, gclid).
//
// The inline script in public/{en,es}/index.html (scripts/partials/lp-head.html) stores
// these values in sessionStorage on landing. The backend has no typed fields for them
// yet, so they travel to the CRM as text in the lead's `observations`
// (Lead.observations is a 512-char column).
export const ATTRIBUTION_STORAGE_KEY = "tfi_attribution";

const ATTRIBUTION_KEYS = [
  "lp",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
] as const;

const SAFE_VALUE = /^[\w.\-~%+ ]{1,100}$/;
const LP_VARIANTS = ["medicare", "carriers", "corta"];
const MAX_LENGTH = 500;
const PREFIX = "[attribution]";

export type Attribution = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>>;

export const readAttribution = (): Attribution => {
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: Attribution = {};
    for (const key of ATTRIBUTION_KEYS) {
      const value = parsed[key];
      if (typeof value !== "string" || !SAFE_VALUE.test(value)) continue;
      if (key === "lp" && !LP_VARIANTS.includes(value)) continue;
      result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
};

// "[attribution] lp=medicare; utm_source=google; gclid=abc" or null when there is none.
export const buildAttributionNote = (): string | null => {
  const attribution = readAttribution();
  const parts = ATTRIBUTION_KEYS.filter((key) => attribution[key]).map(
    (key) => `${key}=${attribution[key]}`
  );
  if (parts.length === 0) return null;
  return `${PREFIX} ${parts.join("; ")}`.slice(0, MAX_LENGTH);
};
